import { useMemo, useState } from 'react';

import { ApolloError, FetchResult, gql, MutationResult, QueryResult } from '@apollo/client';
import { ZonedDateTime } from '@js-joda/core';

import {
  OdProtosWebData_ParticipantInput,
  OdProtosWebData_ParticipantRole,
} from '__generated__/globalTypes';

import { useAuthenticatedQuery, useMutation } from 'platform/apollo';
import { useAuthAuthenticated } from 'platform/auth-fe';
import { useObservability } from 'platform/observability';

import { systemZonedDateTimeFromString } from 'shared/helpers/datetime';

import {
  DeleteParticipant,
  DeleteParticipantVariables,
} from 'sellDirect/graphql/__generated__/DeleteParticipant';
import {
  GetParticipants,
  GetParticipantsVariables,
} from 'sellDirect/graphql/__generated__/GetParticipants';
import {
  ResendParticipantInvite,
  ResendParticipantInviteVariables,
} from 'sellDirect/graphql/__generated__/ResendParticipantInvite';
import {
  UpsertParticipant,
  UpsertParticipantVariables,
} from 'sellDirect/graphql/__generated__/UpsertParticipant';
import { Participant as GqlParticipant } from 'sellDirect/graphql/fragments/__generated__/Participant';
import { ParticipantFragment } from 'sellDirect/graphql/fragments/ParticipantFragment';

// Keep this in sync with MAX_SIGNATORIES in Web Participant model.
// https://github.com/opendoor-labs/web/blob/4d3fe59573f6adadb758398b45a90b809261c511/app/models/participant.rb#L7
export const MAX_SIGNATORIES = 2;

export const GetParticipantsQuery = gql`
  ${ParticipantFragment}
  query GetParticipants($sellerInputUUID: String!) {
    sellDirect {
      getParticipants(input: { lead: { source: { type: RECEPTION, id: $sellerInputUUID } } }) {
        participants {
          participants {
            ...Participant
          }
        }
      }
    }
  }
`;

export const UpsertParticipantMutation = gql`
  ${ParticipantFragment}
  mutation UpsertParticipant(
    $sellerInputUUID: String!
    $participants: [OdProtosWebData_ParticipantInput!]!
  ) {
    sellDirect {
      upsertParticipant(
        input: {
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
          participants: { participants: $participants }
        }
      ) {
        participants {
          participants {
            ...Participant
          }
        }
      }
    }
  }
`;

export const DeleteParticipantMutation = gql`
  ${ParticipantFragment}
  mutation DeleteParticipant($sellerInputUUID: String!, $participantId: String!) {
    sellDirect {
      deleteParticipant(
        input: {
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
          participantId: $participantId
        }
      ) {
        participants {
          participants {
            ...Participant
          }
        }
      }
    }
  }
`;

// Adding __typename in the response because this doesn't get picked up by yarn generate if it doesn't return anything
export const ResendParticipantInviteMutation = gql`
  mutation ResendParticipantInvite($sellerInputUUID: String!, $participantId: String!) {
    sellDirect {
      resendParticipantInvite(
        input: {
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
          participantId: $participantId
        }
      ) {
        __typename
      }
    }
  }
`;

export interface Participant extends Omit<GqlParticipant, 'createdAt'> {
  createdAt: ZonedDateTime; // generated GraphQL type is any, so Omit it and replace with ZonedDateTime

  isInitiator: boolean;
  isSignatory: boolean;
  isCurrentUser: boolean;
}

export interface UseParticipantsResult {
  /** Participants. Undefined if not yet known. */
  participants: Participant[] | undefined;
  /** If current user is initiator of lead. Undefined if not yet known. */
  currentUserIsInitiator: boolean | undefined;
  /** If current user is a participant of a lead. An initiator is also a participant. Undefined if not yet known. */
  currentUserIsParticipant: boolean | undefined;
  /** If another signatory can be added. Max signatories is 2. Undefined if not yet known. */
  canAddSignatory: boolean | undefined;

  getResult: QueryResult<GetParticipants, GetParticipantsVariables>;
  upsertParticipant: (
    participants: OdProtosWebData_ParticipantInput[],
  ) => Promise<FetchResult<UpsertParticipant>>;
  upsertResult: MutationResult<UpsertParticipant>;
  deleteParticipant: (participantId: string) => Promise<FetchResult<DeleteParticipant>>;
  deleteResult: MutationResult<DeleteParticipant>;
  resendParticipantInvite: (participantId: string) => Promise<FetchResult<ResendParticipantInvite>>;
  resendParticipantInviteResult: MutationResult<ResendParticipantInvite>;
}

/**
 * Manages participant state and provides mutation functions.
 */
export const useParticipants: (sellerInputUUID: string) => UseParticipantsResult = (
  sellerInputUUID,
) => {
  const { user: currentHuman } = useAuthAuthenticated();
  const { trackError } = useObservability();
  const [mutatedGqlParticipants, setMutatedGqlParticipants] = useState<GqlParticipant[]>();

  const getResult = useAuthenticatedQuery<GetParticipants, GetParticipantsVariables>(
    GetParticipantsQuery,
    {
      skip: !sellerInputUUID || sellerInputUUID.trim().length === 0,
      variables: {
        sellerInputUUID,
      },
      onCompleted: () => setMutatedGqlParticipants(undefined), // unset any mutated participants
      onError: (error: ApolloError) => {
        // This endpoint will throw an `unauthenticated` error if you
        // are not a participant on the seller input.
        trackError('GetParticipants failed', error);
      },
      context: { linkContext: { team: 'seller-engineering' } },
    },
  );
  const [upsertParticipant, upsertResult] = useMutation<
    UpsertParticipant,
    UpsertParticipantVariables
  >(UpsertParticipantMutation, {
    onCompleted: (data: UpsertParticipant) =>
      setMutatedGqlParticipants(data.sellDirect.upsertParticipant?.participants?.participants),
    onError: (error: ApolloError) => trackError('UpsertParticipant failed', error),
    context: { linkContext: { team: 'seller-engineering' } },
  });
  const [deleteParticipant, deleteResult] = useMutation<
    DeleteParticipant,
    DeleteParticipantVariables
  >(DeleteParticipantMutation, {
    onCompleted: (data: DeleteParticipant) =>
      setMutatedGqlParticipants(data.sellDirect.deleteParticipant?.participants?.participants),
    onError: (error: ApolloError) => trackError('DeleteParticipant failed', error),
    context: { linkContext: { team: 'seller-engineering' } },
  });

  const [resendParticipantInvite, resendParticipantInviteResult] = useMutation<
    ResendParticipantInvite,
    ResendParticipantInviteVariables
  >(ResendParticipantInviteMutation, {
    onCompleted: (data: ResendParticipantInvite) => data,
    onError: (error: ApolloError) => trackError('ResendParticipantInvite failed', error),
    fetchPolicy: 'no-cache',
    context: { linkContext: { team: 'seller-engineering' } },
  });

  const participants = useMemo(() => {
    // Prefer mutated participants, otherwise use original fetched participants.
    const gqlParticipants =
      mutatedGqlParticipants ||
      getResult?.data?.sellDirect.getParticipants?.participants?.participants;

    if (!gqlParticipants) {
      return undefined;
    }

    return (
      gqlParticipants
        // Filter out seller participants
        .filter((p) => p.roles.includes(OdProtosWebData_ParticipantRole.SELLER_PARTICIPANT))
        // Add computed Participant fields.
        .map<Participant>(({ createdAt, ...p }) => {
          return {
            ...p,
            createdAt: systemZonedDateTimeFromString(createdAt),
            isInitiator: p.roles.includes(OdProtosWebData_ParticipantRole.INITIATOR),
            isSignatory: p.roles.includes(OdProtosWebData_ParticipantRole.SIGNATORY),
            isCurrentUser: emailsMatch(p.email, currentHuman?.email),
          };
        })
    );
  }, [currentHuman, getResult, mutatedGqlParticipants]);

  const { currentUserIsInitiator, currentUserIsParticipant } = useMemo(() => {
    if (!participants) {
      return {
        currentUserIsInitiator: undefined,
        currentUserIsParticipant: undefined,
      };
    }

    const curParticipant = participants.find((p) => emailsMatch(p.email, currentHuman?.email));
    if (!curParticipant) {
      return {
        currentUserIsInitiator: false,
        currentUserIsParticipant: false,
      };
    }

    return {
      currentUserIsInitiator: curParticipant.isInitiator,
      currentUserIsParticipant: true,
    };
  }, [currentHuman, participants]);

  const canAddSignatory = useMemo(() => {
    if (!participants) {
      return undefined;
    }

    const signatories = participants.filter((p) => p.isSignatory);
    return signatories.length < MAX_SIGNATORIES;
  }, [participants]);

  return {
    participants,
    canAddSignatory,
    currentUserIsInitiator,
    currentUserIsParticipant,
    getResult,
    upsertParticipant: (participants: OdProtosWebData_ParticipantInput[]) =>
      upsertParticipant({
        variables: {
          sellerInputUUID,
          participants,
        },
      }),
    upsertResult,
    deleteParticipant: (participantId: string) =>
      deleteParticipant({
        variables: {
          sellerInputUUID,
          participantId,
        },
      }),
    deleteResult,
    resendParticipantInvite: (participantId: string) =>
      resendParticipantInvite({
        variables: {
          sellerInputUUID,
          participantId,
        },
      }),
    resendParticipantInviteResult,
  };
};

/** Returns true if both strings are present and compares them case insensitively. */
const emailsMatch = (s1: string | null | undefined, s2: string | null | undefined): boolean => {
  if (!s1 || !s2) {
    return false;
  }
  return s1.toLowerCase() === s2.toLowerCase();
};
